import { useState } from 'react'
import CapstonMark from '../components/CapstonMark'
import { api } from '../lib/api'

// Magic sign-in link landing page (#27). The emailed URL points here — a
// plain frontend page — and the single-use token is only consumed by the
// explicit button's POST. That is the email-scanner defense: a security
// scanner prefetching the link with a GET burns nothing.
//
// A spent link offers to request a new one; where that leads is App.jsx's
// call (onRequestNewLink), not this page's — it knows nothing about /admin.
export default function VerifyPage({ token, onVerified, onRequestNewLink }) {
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState(null)
  const [retryable, setRetryable] = useState(false)

  async function handleVerify() {
    if (submitting) return
    setSubmitting(true)
    setError(null)
    try {
      const me = await api('/api/v1/auth/verify', {
        method: 'POST',
        body: { token },
      })
      onVerified(me)
    } catch (err) {
      // 429 (or a network blip) can succeed on retry; a 401 means the link
      // is spent/expired and only a fresh one will do.
      setRetryable(err.status === 429 || err.status === 0)
      setError(err.detail || 'This sign-in link is no longer valid.')
      setSubmitting(false)
    }
  }

  return (
    <main
      className="min-h-screen flex items-center justify-center px-4"
      style={{ background: 'var(--color-surface-alt)' }}
    >
      <div className="card card--panel w-full" style={{ maxWidth: '420px' }}>
        <CapstonMark size={32} />
        <p className="overline" style={{ marginTop: 'var(--space-m)' }}>Capston</p>
        <h1 className="text-h3" style={{ marginBottom: 'var(--space-m)' }}>
          Experience
        </h1>

        {error && (
          <div
            className="alert alert--error text-body-sm"
            style={{ marginBottom: 'var(--space-s)' }}
            role="alert"
          >
            {error}
          </div>
        )}

        {error && !retryable ? (
          <div className="flex flex-col" style={{ gap: 'var(--space-s)' }}>
            <p className="text-body-md" style={{ color: 'var(--color-text-secondary)' }}>
              Sign-in links work once and expire — request a fresh one to
              continue.
            </p>
            <button type="button" className="btn btn--primary" onClick={onRequestNewLink}>
              Request a new link
            </button>
          </div>
        ) : (
          <div className="flex flex-col" style={{ gap: 'var(--space-s)' }}>
            <p className="text-body-md" style={{ color: 'var(--color-text-secondary)' }}>
              Click below to finish signing in.
            </p>
            <button
              type="button"
              className="btn btn--primary"
              onClick={handleVerify}
              disabled={submitting}
            >
              {submitting ? 'Signing in…' : 'Complete sign-in'}
            </button>
          </div>
        )}
      </div>
    </main>
  )
}
